import { Component, OnInit, Inject, Input } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { catchError, filter } from 'rxjs/operators';

import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../../../shared/dialogs/components/confirmation-dialog.component';
import { MessageDialogComponent } from '../../../../shared/dialogs/components/message-dialog.component';

import { GestionAccesGenericComponentBase } from './gestion-acces-generic.component-base';

import { AccesSearchResult } from '../entities/acces-search-result';
import { AccesActionRequest } from '../entities/acces-action-request';

import { CoactiviteDecision } from '../entities/coactivite-decision.constants';
import { EventBusService, Events } from '../../../../technical-modules/events/services/event-bus.service';
import { JSR310ConversionService } from '../../../../shared/jsr310-localdates/services/jsr310-conversion.service';

import { ErrorAccesOrder } from '../entities/error-acces-order.entity';

import { AccesService } from '../services/acces.service';
import { UserService } from '../../../humain/user/services/user.service';

import { AccesEtat, AccesAction, applicationConstants } from '../../../../app.constants';

@Component({
	selector: 'gestion-acces',
	templateUrl: 'gestion-acces.html',
	// styleUrls: ['gestion-liste-acces.css'],
})
export class GestionAccesComponent extends GestionAccesGenericComponentBase<AccesSearchResult> implements OnInit {
	displayRappelIntervenantsCommands = new Subject<number>();
	private accesEnAutorisation!: AccesSearchResult;
	
	constructor(
		private dialog: MatDialog,

		@Inject(EventBusService) eventBusService: EventBusService,
		@Inject(JSR310ConversionService) jsr310Service: JSR310ConversionService,

		@Inject(AccesService) accesService: AccesService,
		@Inject(UserService) userService: UserService,
	) {
		super(eventBusService, jsr310Service, accesService, userService);
		this.title = 'Liste des accès';
	}
	
	// Fonction permettant de determiner si un acces fait partie d'un tarif negatif
	// Cette logique est isolee en une fonction a part, car il est probable qu'elle
	// evolue dans le futur, si d'autres batch de creation de masse apparaissent
	// Controler le jobExecutionId ne suffira alors plus
	//
	// Idealement il faudrait regarder les actes O&M a la place mais cela ralentirait
	// pour le moment le chargement
	protected isAccesTarifNegatif(acces: AccesSearchResult) {
		return acces.jobExecutionId != null;
	}
	
	override onActionRequest(accesActionRequest: AccesActionRequest<AccesSearchResult>): void {
		switch (accesActionRequest.request) {
			case AccesAction.ASSOCIER_ARRETS:
				this.displayAssociationArretsCommands.next([accesActionRequest.acces.id]);
				break;
			case AccesAction.ASSOCIER_OPERATIONS:
				this.eventBusService.emit(Events.AssociationOperationsModalRequested, accesActionRequest.acces.id);
				break;
			case AccesAction.AUTORISER:
			case AccesAction.OUVRIR_CONTROLE_SECURITE:  
	            this.authorize(accesActionRequest.acces);
	            break;
			case AccesAction.CLASSER_EN_ERREUR:
				this.markAsError(accesActionRequest.acces);
				break;
			case AccesAction.DUPLIQUER:
				this.eventBusService.emit(Events.DuplicateAccesModalRequested, accesActionRequest.acces.id);
				break;
			case AccesAction.EXPORTER:
				this.exporter(accesActionRequest.etat);
				break;
			case AccesAction.FERMER:
				this.displayAccesFermetureCommands.next(accesActionRequest.acces.id);
				break;
			case AccesAction.INDIQUER_RAPPEL:
				this.displayRappelIntervenantsCommands.next(accesActionRequest.acces.id);
				break;
			case AccesAction.MODIFIER:
				let acces = accesActionRequest.acces;
				if (this.isAccesTarifNegatif(acces)) {
					// Si l'acces fait partie d'un tarif negatif, il faut appeler
					// le dialogue specifique a ce genre de cas
					this.modificationTarifNegatifCallSubject$.next([acces.id]);
				} else {
					// Sinon on appele 
					this.eventBusService.emit(Events.EditAccesModalRequested, acces.id);
				}
				break;
			case AccesAction.REFUSER:
				this.displayRefusAccesCommands.next([accesActionRequest.acces.id]);				
				break;
			case AccesAction.SUPPRIMER:
				this.supprimerAcces(accesActionRequest.acces.id);
				break;
			default:
				// rien à faire ici
		}
	}
	
	authorize(acces: AccesSearchResult) {
	    this.accesEnAutorisation = acces;
	    setTimeout(() => this.displayControleSecuriteCommands.next(acces.id));
	}
	
    onControleSecuriteDecision(acces: AccesSearchResult) {
        // confier la suite des operations au checker adapté
		if(acces.parcEnergie === 'Solaire') {
			this.checkCoactiviteSolaireCommands.next({
				accesId: acces.id,
				ouvragesIds: acces.ouvrageIds.split(",").map($ => +$),
				isUserCac: this.user.id === acces.cacId,
				isUserCc: this.user.id === acces.ccId && acces.typeAcces === 'A_DISTANCE',
				controleSecurite: acces.controleSecurite,
			});
		} else if (acces.parcEnergie === 'Eolienne') {
			this.checkCoactiviteEolienCommands.next({
			accesId: acces.id,
			accesCegex: acces.accesCegex,
			ouvrageId: +acces.ouvrageIds.split(",")[0],
			isUserCac: this.user.id === acces.cacId,
			isUserCc: this.user.id === acces.ccId && acces.typeAcces === 'A_DISTANCE',
			controleSecurite: acces.controleSecurite,
			});
		}
		
	}
		
	onCoactiviteDecision(accesId: number | null, decision: CoactiviteDecision, controleSecurite: number | null) {
	    if (accesId === null) {
	        throw new Error("accesId non recu du coactivite checker");
	    }

	    if (decision === CoactiviteDecision.AUTHORIZE) {
	        this.persistAccesAutorization(accesId, decision, controleSecurite).subscribe(
	            this.callAccesListRefresh
	        );
	    } else if (decision === CoactiviteDecision.DENY) {
	        this.persistAccesDenial(accesId, 4).subscribe(
	            (acces) => {
	                this.dialog.open(MessageDialogComponent, {
	                    width: '400px',
	                    data: {
	                        title: 'Accès refusé pour coactivité',
	                        messages: acces.parcCode + ' - ' + acces.numero
	                    }
	                });
	                this.callAccesListRefresh();
	            }
	        );
	    } else if (decision === CoactiviteDecision.ASK_VALIDATION) {
	        this.demanderValidation(accesId);
	    } else if (decision === CoactiviteDecision.AUTHORIZE_WITH_FORCE) {
	        this.persistAccesAutorization(accesId, decision, controleSecurite).subscribe(
	            this.callAccesListRefresh
	        );
	    }
	}

	private demanderValidation(accesId: number) {
		this.accesService.demanderValidation(accesId).pipe(
			catchError(
				(response: HttpErrorResponse) => {
					if (response.error && response.error.message) {
						alert(response.error.message);
					} else {
						alert("Erreur lors de la demande de validation de cet accès (" + response.status + ")");
					}
					throw new Error();
				}
			)
		).subscribe(
			(_resultat: any) => {
				console.log('demande validation poussée: ', _resultat);
				this.callAccesListRefresh();
			}
		);
	}
	
	markAsError(acces: AccesSearchResult) {
		let dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			width: '600px',
			data: {
				title: 'Confirmation',
				message: "Confirmez vous l'erreur de saisie pour cet accès ?",
			}
		});

		dialogRef.afterClosed().pipe(
			filter(accepted => accepted)
		).subscribe(
			// Appeler le serveur pour lancer la modification
			_ => {
				let date = this.jsr310Service.toMandatoryLocalDateTime(new Date());
		
				let command = new ErrorAccesOrder(
					acces.id,
					date,
					'',
				);
				
				this.accesService.updateForError(command).subscribe(
					this.callAccesListRefresh,
					(httpResponse: HttpErrorResponse) => {
						let errMsg =  (httpResponse && httpResponse.error && httpResponse.error['message']) ?
							httpResponse.error['message']:
							'Le serveur a rencontré une erreur imprévue'
						;
						this.dialog.open(MessageDialogComponent, {
							width: '400px',
							data: {
								title: 'Enregistrement impossible',
								messages: [errMsg]
							}
						});
						console.error("Erreur lors la tentivate d'enregistrement de l'acces #" + acces.id + " au statut annule", httpResponse);
					}
				);
			}
		);
	}
	
	onRappelIntervenantsChanged() {
		this.callAccesListRefresh();
	}

	protected override getExportFileName(etat: AccesEtat | null): string {
		let etatLabel = etat === null ? 'tous-les-etats' : applicationConstants.etatAcces[etat];
		let fileName = 'export-acces-' + etatLabel + '.xls';
		return fileName;
	}
	
	onControleSecuriteAutorisation(controleSecurite: number | null) {
	    this.accesEnAutorisation.controleSecurite = controleSecurite;
	    this.onControleSecuriteDecision(this.accesEnAutorisation);
	}
}